import { InitOptions , BackendModule, ReadCallback, Services } from 'i18next'

interface TranslationManifest {
  [key: string]: string;
}

export class HashedHttpBackend implements BackendModule<object> {
  private manifestPromise: Promise<TranslationManifest> | null = null
  private services: Services | null = null
  private options: InitOptions = {}
  private isProduction = process.env.NODE_ENV === 'production'

  constructor(options?: InitOptions) {
    if (options) {
      this.options = options
    }
  }

  init(services: Services, options: InitOptions = {}): void {
    this.services = services
    this.options = { ...this.options, ...options }

    if (this.isProduction) {
      // Initialize the manifest promise but don't await it
      this.manifestPromise = this.loadManifest()
    }
  }

  private async loadManifest(): Promise<TranslationManifest> {
    try {
      const response = await fetch('/translations-manifest.json')
      return await response.json()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Failed to load translations manifest: ${errorMessage}`)
    }
  }

  async read(language: string, namespace: string, callback: ReadCallback): Promise<void> {
    try {
      if (this.isProduction) {
        // Use the existing promise or create it if it doesn't exist
        if (!this.manifestPromise) {
          this.manifestPromise = this.loadManifest()
        }

        const manifest = await this.manifestPromise
        const key = `${language}/${namespace}.json`
        const hashedPath = manifest[key]

        if (!hashedPath) {
          throw new Error(`No hashed path found for ${key}`)
        }

        const response = await fetch(`/locales/${hashedPath}`)
        if (!response.ok) {
          throw new Error(`Failed to load translation file: ${response.statusText}`)
        }

        const data = await response.json()
        callback(null, data)
      } else {
        // Development mode: Use regular translation files
        const response = await fetch(`/locales/${language}/${namespace}.json`)
        if (!response.ok) {
          throw new Error(`Failed to load translation file: ${response.statusText}`)
        }

        const data = await response.json()
        callback(null, data)
      }
    } catch (error) {
      const errorInstance = error instanceof Error ? error : new Error('Unknown error')
      callback(errorInstance, null)
    }
  }

  create(): void {
    // Required method for i18next backend interface
  }

  type = 'backend' as const
}
